import { awsRum } from 'src/serviceClients/aws-rum/awsRum-Service';
import { ClientSideMetricsConstants } from 'src/constants/client-side-metrics-constants';

// The tone values UX set, to keep identical; with a flat gain, the onset and offset clicks are part of each sound
const TONES = {
    ok: { frequency: 880, gain: 0.08, seconds: 0.09, type: 'sine' },
    err: { frequency: 220, gain: 0.1, seconds: 0.18, type: 'square' }
} as const;

export type ScanTone = keyof typeof TONES;

// Chosen well under the time between two scans: a tone that cannot start within it is dropped, never played late
const MAX_START_DELAY_MS = 100;

// One context for the page: browsers cap how many a document may create
let audioContext: AudioContext | undefined;

const getAudioContext = (): AudioContext => {
    if (!audioContext || audioContext.state === 'closed') {
        audioContext = new AudioContext();
    }
    return audioContext;
};

// Why a tone did not play: each value is the Reason the ScanTone.Unavailable event records
enum DropReason {
    NO_AUDIO = 'NO_AUDIO',
    RESUME_FAILED = 'RESUME_FAILED',
    TOO_LATE = 'TOO_LATE',
    THREW = 'THREW'
}

// A dropped tone is silent, so these events are the only production signal that devices hear the tones
const recordToneUnavailable = (tone: ScanTone, reason: DropReason) => {
    try {
        awsRum.recordEvent(ClientSideMetricsConstants.SCAN_TONE_UNAVAILABLE, { Tone: tone, Reason: reason });
    } catch (error) {
        // A metric that fails must not fail the scan
    }
};

const playTone = (audio: AudioContext, tone: ScanTone) => {
    const { frequency, gain, seconds, type } = TONES[tone];
    const oscillator = audio.createOscillator();
    const volume = audio.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    volume.gain.value = gain;
    oscillator.connect(volume).connect(audio.destination);
    oscillator.start();
    oscillator.stop(audio.currentTime + seconds);
};

// Call on each keystroke in a scan field: a keystroke is a user gesture, so the context starts then and is running when
// the scan's answer sounds its tone. One first created for that tone either misses the start bound, and the tone is
// dropped, or starts while the audio output is still opening, which cuts off the tone's onset. Never throws
export const primeScanAudio = (): void => {
    try {
        const audio = getAudioContext();
        if (audio.state !== 'running') {
            audio.resume().catch(() => undefined);
        }
    } catch (error) {
        // Without audio there is nothing to start, and the tone records why it did not play
    }
};

// Never throws: a device without audio still shows every scan's result
export const playScanTone = (tone: ScanTone): void => {
    let audio: AudioContext;
    try {
        audio = getAudioContext();
    } catch (error) {
        recordToneUnavailable(tone, DropReason.NO_AUDIO);
        return;
    }
    try {
        if (audio.state === 'running') {
            playTone(audio, tone);
            return;
        }
        // A browser that will not start the context never answers its resume, so whichever comes first, the answer or
        // the deadline, decides the tone, and whatever comes after does nothing
        let waiting = true;
        const runOnce = (then: () => void) => () => {
            if (waiting) {
                waiting = false;
                then();
            }
        };
        audio.resume()
            .then(runOnce(() => playTone(audio, tone)),
                runOnce(() => recordToneUnavailable(tone, DropReason.RESUME_FAILED)))
            .catch(() => recordToneUnavailable(tone, DropReason.THREW));
        setTimeout(runOnce(() => recordToneUnavailable(tone, DropReason.TOO_LATE)), MAX_START_DELAY_MS);
    } catch (error) {
        recordToneUnavailable(tone, DropReason.THREW);
    }
};
